/**
 * Talking to the RFQ workflow endpoints. Everything here requires a signed-in
 * user — an RFQ belongs to an account — and the page handles the sign-in gate
 * before calling any of it.
 */
import { sendAuthenticatedRequest } from "../common/functions";
import type { ActivityActor, ActivityEntry } from "../components/ActivityTrail";
import { Address } from "../common/models/company";

const BASE = `${process.env.REACT_APP_BF_API_BASE_URL}/rfqs`;

export type RfqLine = {
  id: string;
  rawText: string;
  material: string;
  /** The governing specification (required). */
  standard: string;
  /** The standard was proposed by the system, not typed by the customer; cleared on edit. */
  standardSuggested?: boolean;
  shape: string;
  dimensions: { [dimension: string]: string };
  quantity: string;
  quantityUnit: string;
  condition: string;
  /** The customer takes the material with or without this condition. */
  conditionOptional?: boolean;
  originCompliance: string;
  additionalSpecifications: string;
  additionalWork: string;
  targetPrice: string;
  /**
   * Kono-internal: the operator's explicit choice of which inventory item
   * fills this line. Empty means "use the best match".
   */
  preferredProductId?: string;
  /** Kono-internal: why that vendor was picked, in the operator's words. */
  preferredProductReason?: string;
  preferredProductBy?: string;
  preferredProductAt?: string;
  /** Operator's extra items offered on the quote next to the chosen one. */
  alsoOfferProductIds?: string[];
  /** The item this line was last quoted with, and in which quote. */
  quotedIn?: {
    quoteId: string;
    quoteReference: string;
    productId: string;
    productSummary: string;
    supplierName: string;
    unitPrice: number | null;
    currency: string;
    at: string;
  } | null;
  /**
   * Kono-internal: the operator's explicit choice of vendors to RFQ for this
   * line. null/undefined = decide automatically; an array = email exactly
   * these vendors (a non-empty array also forces vendor sourcing on submit).
   */
  vendorIdsToContact?: string[] | null;
  missingFields: string[];
};

export type RfqCustomer = {
  name: string;
  company: string;
  email: string;
  phone: string;
  /** Ship-to used to rate freight on the generated quote. */
  address?: Address | null;
  /** The customer's website as checked after parsing. */
  website?: RfqCustomerWebsite | null;
};

export type RfqCustomerWebsite = {
  url: string;
  domain: string;
  status: "verified" | "unreachable" | "parked" | "freemail" | "unknown";
  title: string;
  summary: string;
  relevant: boolean | null;
  relevance: string;
  checkedAt: string;
};

export type RfqStatus =
  | "draft"
  | "submitted"
  | "quoted"
  | "waiting-vendors"
  | "closed"
  | "converted";

export type RfqPriority = "price" | "lead-time" | "show-all";

export type Rfq = {
  id: string;
  ownerUid: string;
  ownerEmail: string;
  companyDomain?: string | null;
  activity?: ActivityEntry[];
  ownerIsKonoTeam: boolean;
  customer: RfqCustomer;
  priority: RfqPriority;
  status: RfqStatus;
  lines: RfqLine[];
  neededBy: string;
  deliveryLocation: string;
  notes: string;
  sourceFile: { filename: string; filetype: string } | null;
  /** Customer's original paste or file-derived extract prompt. */
  sourceText?: string;
  createdAt: string;
  updatedAt: string;
  quoteId: string | null;
  quoteReference: string | null;
  vendorDeadlineAt: string | null;
  unknownLineIds?: string[];
  rejectedLineIds?: string[];
  operatorFulfillmentChoice?: "send-now" | "send-at-deadline" | "hold" | null;
  fullFulfillmentNotifiedAt?: string | null;
  goodwillCouponCode?: string | null;
  deadlinePartialNotifiedAt?: string | null;
  partialCustomEmailDraft?: string | null;
  partialWatchUntil?: string | null;
  /** Set when the RFQ was submitted but no vendor was emailed: an operator has to pick them. */
  vendorSelectionPendingAt?: string | null;
  /** The last "Generate quote" that found nothing quotable, and what it found instead. */
  lastQuoteAttempt?: RfqQuoteAttempt | null;
  /** Why a closed RFQ closed ("deadline", "operator"). */
  closedReason?: string | null;
  /** A vendor beat the quote after it was generated (operators). */
  betterPrice?: BetterPrice | null;
  /** Lines an edit took off the request: what they were, who removed them, when. */
  removedLines?: RfqRemovedLine[];
  /** The Kono operator responsible (internal RFQs page "Assigned"). */
  assignedTo?: { email: string; at: string; by: string } | null;
  /** Set when the request arrived by email in a Kono mailbox. */
  mailboxSource?: { mailbox: string; from: string; subject: string; receivedAt: string } | null;
  submittedAt?: string | null;
  closedAt?: string | null;
};

export type RfqRemovedLine = {
  id: string;
  description: string;
  removedAt: string;
  removedBy: string;
};

/** What a "Generate quote" that produced no quote found, per unquotable line. */
export type RfqQuoteAttempt = {
  at: string;
  by: string;
  lines: Array<{
    lineId: string;
    description: string;
    rejected: Array<{
      productId: string;
      supplierId: string | null;
      supplierName: string | null;
      summary: string;
      price: string | null;
      reasons: string[];
    }>;
  }>;
};

/** An open lower price option on the RFQ's quote, as the lists show it. */
export type BetterPriceSummary = {
  quoteReference: string;
  savings: number;
  currency: string;
  lines: number;
  nowAvailable: number;
  vendors: string[];
  detectedAt: string;
};

export type RfqSummary = {
  id: string;
  createdAt: string;
  updatedAt: string;
  firstMaterial: string;
  lineCount: number;
  status: RfqStatus;
  betterPrice?: BetterPriceSummary | null;
  complete: boolean;
  quoteId: string | null;
  quoteReference: string | null;
  vendorDeadlineAt: string | null;
  // Present only in the operators' all-customers list.
  ownerEmail?: string;
  ownerIsKonoTeam?: boolean;
  customerEmail?: string;
  customerCompany?: string;
  // Who filed it and the latest change, for the company-wide customer list.
  createdBy?: ActivityActor | null;
  lastActivity?: ActivityEntry | null;
  activity?: ActivityEntry[];
  /** A coworker's RFQ, visible because you share a business domain. */
  byCoworker?: boolean;
};

export type RfqExtraction = {
  customer: RfqCustomer;
  lines: RfqLine[];
  neededBy: string;
  deliveryLocation: string;
  notes: string;
  sourceText?: string;
};

export type BuyerLineOption = {
  productId: string;
  summary: string | null;
  price: { value: number; unit?: string; currency?: string } | null;
  lineAmount: number | null;
  leadTimeDaysMin: number | null;
  leadTimeDaysMax: number | null;
  exactMatch: boolean;
  differences: string[];
  notes: string[];
  selected: boolean;
};

export type BuyerLineOptions = {
  lineId: string;
  verdict: "exact" | "close" | "unavailable";
  options: BuyerLineOption[];
};

export type RfqInlineQuote = {
  id: string;
  reference: string;
  cartItems: unknown[];
  totals: {
    productSubtotal: number;
    shippingTotal: number;
    totalPrice: number;
    currency: string;
    itemsMissingPrice?: number;
    productSubtotalBeforeDiscount?: number;
    volumeDiscount?: {
      matchingSubtotal: number;
      discountPercent: number;
      discountAmount: number;
      threshold: number;
    } | null;
  };
};

export type SubmitOutcome =
  | {
      outcome: "quoted";
      quoteId: string;
      quoteReference: string;
      quote?: RfqInlineQuote | null;
      allowAddToCart?: boolean;
      allowSaveQuote?: boolean;
      showOptions?: boolean;
      showQuoteImmediately?: boolean;
      options?: BuyerLineOptions[];
      quotedLineIds?: string[];
      remainingLineIds?: string[];
      keepSourcing?: boolean;
    }
  | {
      outcome: "choose-options";
      quoteId: string | null;
      quoteReference: string | null;
      quote?: RfqInlineQuote | null;
      allowAddToCart?: boolean;
      allowSaveQuote?: boolean;
      showOptions?: boolean;
      showQuoteImmediately?: boolean;
      options?: BuyerLineOptions[];
    }
  | {
      outcome: "quoted-partial";
      quoteId: string | null;
      quoteReference: string | null;
      vendorsContacted: number;
      deadlineAt: string | null;
      maxQuoteBusinessHours: number;
      unavailableLineIds: string[];
      rejectedLineIds?: string[];
      unknownLineIds?: string[];
      comebackBusinessDays?: number | null;
      allowAddToCart?: boolean;
      allowSaveQuote?: boolean;
      showOptions?: boolean;
      options?: BuyerLineOptions[];
      quote?: RfqInlineQuote | null;
    }
  | {
      outcome: "waiting-vendors";
      vendorsContacted: number;
      deadlineAt: string | null;
      maxQuoteBusinessHours: number;
      unavailableLineIds: string[];
      rejectedLineIds?: string[];
      unknownLineIds?: string[];
      unknownItemsHeldForReview?: boolean;
    };

export const messageOf = (e: unknown): string =>
  e instanceof Error ? e.message : "Something went wrong";

const parseOrThrow = async (response: Response): Promise<any> => {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body?.error || `Request failed (${response.status})`);
  }
  return body;
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const extractJobLooksTransient = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error || "");
  return /\b502\b|\b504\b|\b503\b|Failed to fetch|NetworkError|temporary error/i.test(
    message,
  );
};

const pollExtractJob = async (jobId: string): Promise<RfqExtraction> => {
  const deadline = Date.now() + 5 * 60 * 1000;
  while (Date.now() < deadline) {
    await sleep(2000);
    const response = await sendAuthenticatedRequest(
      `${BASE}/extract-jobs/${encodeURIComponent(jobId)}`,
      { method: "GET" },
    );
    const body = await parseOrThrow(response);
    if (body?.status === "done" && body.extraction) {
      return body.extraction;
    }
    if (body?.status === "error") {
      throw new Error(body.error || "The document could not be read");
    }
  }
  throw new Error(
    "Reading this document is taking longer than expected. Please try again.",
  );
};

export const extractRfq = async (params: {
  filename?: string;
  filetype?: string;
  data?: string;
  text?: string;
}): Promise<RfqExtraction> => {
  const started = await sendAuthenticatedRequest(`${BASE}/extract/start`, {
    method: "POST",
    jsonBody: params,
  });
  const { jobId } = await parseOrThrow(started);
  if (!jobId) throw new Error("Could not start reading the document");
  try {
    const run = await sendAuthenticatedRequest(
      `${BASE}/extract-jobs/${encodeURIComponent(jobId)}/run`,
      { method: "POST", jsonBody: {} },
    );
    const body = await parseOrThrow(run);
    if (body?.extraction) return body.extraction;
  } catch (error) {
    if (!extractJobLooksTransient(error)) throw error;
  }
  return pollExtractJob(jobId);
};

export const listRfqs = async (
  scope: "mine" | "all" = "mine",
): Promise<RfqSummary[]> => {
  const response = await sendAuthenticatedRequest(
    scope === "all" ? `${BASE}?scope=all` : BASE,
    { method: "GET" },
  );
  return (await parseOrThrow(response)).rfqs;
};

/** One vendor's outreach state, visible to Kono operators only. */
export type RfqVendorRequestRow = {
  id: string;
  supplierName: string;
  emails: string[];
  status: "sent" | "responded" | "declined" | "closed";
  sentAt: string | null;
  followUpsSent: number;
  /** The last email we sent this vendor on the RFQ (outreach, scheduled follow-up, or one sent by hand). */
  lastEmailAt?: string | null;
  /** When the scheduler plans the next chase; null when it will not chase again. */
  nextFollowUpAt?: string | null;
  /** Set while the outreach itself is still queued (outside business hours). */
  queuedUntil?: string | null;
  /** Follow-ups an operator sent by hand from the workspace. */
  manualFollowUps?: { at: string; by: string; subject: string; to: string[]; from: string; sent: boolean; followUpNumber: number }[];
  /** When the system recognized the reply (the mailbox read). */
  respondedAt: string | null;
  /** When the vendor's email was received (its own date header). */
  responseReceivedAt?: string | null;
  responseSummary: string;
  /** Why they said no: no-material | no-size | no-stock | needs-info | policy | other. */
  declineKind?: string | null;
  declineReason?: string | null;
  /** The vendor's reply in their own words. */
  responseText?: string | null;
  responseFrom?: string | null;
  /** "thread/thread-only" when a partner answered for the vendor we asked. */
  responseMatchedVia?: string | null;
  /** The follow-up question already sent after their decline, if any. */
  declineReply?: { at: string; by: string; subject: string; body: string; to: string[]; from: string; sent: boolean } | null;
  /** The last reply we sent to this vendor's answer (thank-you or ask-why). */
  vendorReply?: VendorReplySent | null;
  materials: string[];
  /** The exact subject the RFQ went out with. */
  subject?: string;
  email?: string;
  /** Every email we sent this vendor for the request (RFQ, follow-ups), oldest first; kept since Nov 2026. */
  emailLog?: RfqVendorEmailLogEntry[];
};

export type RfqVendorEmailLogEntry = {
  kind: "rfq" | "follow-up";
  at: string;
  by: string;
  subject: string;
  text: string;
  to: string[];
  cc?: string[];
  from: string;
  sent: boolean;
};

export type VendorReplyKind = "thank-you" | "ask-why" | "follow-up";
export type VendorReplySent = {
  at: string;
  by: string;
  kind: VendorReplyKind;
  subject: string;
  body: string;
  to: string[];
  from: string;
  sent: boolean;
  edited?: boolean;
};

export type VendorReplyDraft = {
  subject: string;
  body: string;
  to: string[];
  kind: VendorReplyKind;
  /** "standard" | "vendor:offer" | "vendor:question" | "edits:vendor" | "edits:kind": how the proposal was arrived at. */
  basis: string;
  /** How many earlier operator edits shaped the proposal (0 for the standard text). */
  examplesUsed: number;
  /** One line on what the vendor offered or asked, when the proposal was written around it. */
  gist?: string;
};
/** @deprecated use VendorReplyDraft */
export type DeclineReplyDraft = VendorReplyDraft;

export type VendorReplySender = "operator" | "team";

export type VendorReplyProposal = {
  draft: VendorReplyDraft;
  from: string;
  cc: string | null;
  sender: VendorReplySender;
  senders: { operator: string; team: string };
};

export type StandardSuggestion = {
  standard: string;
  note: string;
  source: "table" | "cache" | "llm";
};

/** The specification to propose for a material + shape (+ condition); null when none is the clear default. */
export const suggestStandard = async (input: {
  material: string;
  shape: string;
  condition?: string;
}): Promise<StandardSuggestion | null> => {
  const response = await sendAuthenticatedRequest(`${BASE}/standard-suggest`, {
    method: "POST",
    jsonBody: input,
  });
  const data = (await parseOrThrow(response)) as { suggestion: StandardSuggestion | null };
  return data.suggestion || null;
};

/**
 * Operators: the proposed reply to a vendor's answer (thank-you after a
 * quote, "why" after a decline), ready to edit. `sender` picks whose
 * mailbox it would go out from: the operator's own (default) or the team's.
 */
export const fetchVendorReplyDraft = async (
  rfqId: string,
  requestId: string,
  sender: VendorReplySender = "operator",
): Promise<VendorReplyProposal> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${rfqId}/vendor-requests/${requestId}/decline-reply?from=${sender}`,
    { method: "GET" },
  );
  return parseOrThrow(response);
};
/** @deprecated use fetchVendorReplyDraft */
export const fetchDeclineReplyDraft = fetchVendorReplyDraft;

/**
 * Operators: send the (edited) reply. `proposed` is the text the operator
 * was shown, so the server can learn from what they changed.
 */
export const sendVendorReply = async (
  rfqId: string,
  requestId: string,
  draft: VendorReplyDraft,
  options: { sender: VendorReplySender; proposed?: { subject: string; body: string } },
): Promise<{ sent: boolean; from: string; to: string[]; kind: VendorReplyKind; learned: boolean }> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${rfqId}/vendor-requests/${requestId}/decline-reply`,
    {
      method: "POST",
      jsonBody: {
        to: draft.to,
        subject: draft.subject,
        body: draft.body,
        basis: draft.basis,
        from: options.sender,
        proposedSubject: options.proposed?.subject,
        proposedBody: options.proposed?.body,
      },
    },
  );
  return parseOrThrow(response);
};
/** @deprecated use sendVendorReply */
export const sendDeclineReply = (rfqId: string, requestId: string, draft: VendorReplyDraft) =>
  sendVendorReply(rfqId, requestId, draft, { sender: "operator" });

/**
 * Operators: the follow-up the scheduler would send a vendor who has not
 * answered, to edit and send now. Same shape as a reply proposal, with
 * `kind: "follow-up"`; `followUpNumber` says which chase this would be.
 */
export const fetchVendorFollowUpDraft = async (
  rfqId: string,
  requestId: string,
  sender: VendorReplySender = "operator",
): Promise<VendorReplyProposal & { draft: VendorReplyDraft & { followUpNumber: number; lastEmailAt: string | null } }> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${rfqId}/vendor-requests/${requestId}/follow-up?from=${sender}`,
    { method: "GET" },
  );
  return parseOrThrow(response);
};

/** Operators: send the (edited) follow-up now. Counted like a scheduled follow-up; the next scheduled chase moves out from it. */
export const sendVendorFollowUp = async (
  rfqId: string,
  requestId: string,
  draft: Pick<VendorReplyDraft, "to" | "subject" | "body">,
  options: { sender: VendorReplySender },
): Promise<{ sent: boolean; from: string; to: string[]; kind: VendorReplyKind; learned: boolean; followUpNumber: number }> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${rfqId}/vendor-requests/${requestId}/follow-up`,
    { method: "POST", jsonBody: { to: draft.to, subject: draft.subject, body: draft.body, from: options.sender } },
  );
  return parseOrThrow(response);
};

/* ------------------------------------------------- lower price option */

export type BetterPriceLine = {
  lineId: string;
  requested: string;
  kind: "cheaper" | "now-available";
  quotedProductId: string | null;
  quotedSummary: string;
  quotedSupplierName: string;
  quotedLineAmount: number | null;
  newProductId: string;
  newSummary: string;
  newSupplierId: string;
  newSupplierName: string;
  newLineAmount: number;
  newUnitPrice: number | null;
  newPriceUnit: string;
  currency: string;
  savings: number;
  foundAt: string;
};

export type BetterPriceProposal = { subject: string; text: string; to: string; cc: string[]; model: string; at: string; by: string };

/**
 * A vendor answered after the quote was generated with a better price (or
 * an item the quote could not cover). Operators see it on the RFQ, Quotes
 * and Sales > Quotes; the customer hears about it when the new version is sent.
 */
export type BetterPrice = {
  status: "open" | "revised" | "dismissed";
  detectedAt: string;
  updatedAt: string;
  quoteId: string;
  quoteReference: string;
  customerHadQuote: boolean;
  vendors: { requestId: string; supplierId: string; supplierName: string; respondedAt: string | null; late: boolean }[];
  lines: BetterPriceLine[];
  savings: number;
  currency: string;
  proposal: BetterPriceProposal | null;
  revisedQuoteId: string | null;
  revisedQuoteReference: string | null;
  revisedAt: string | null;
  revisedBy: string | null;
  dismissedAt: string | null;
  dismissedBy: string | null;
  dismissReason: string | null;
  notifiedAt: string | null;
};

/** The short flag a quote carries (quotes list and quote page, operators only). */
export type QuoteBetterPriceFlag = {
  status: "open" | "revised" | "dismissed";
  rfqId: string;
  detectedAt: string;
  updatedAt: string;
  lines: number;
  savings: number;
  currency: string;
  vendors: string[];
  revisedQuoteId: string | null;
  revisedQuoteReference: string | null;
};

export type BetterPriceView = {
  betterPrice: BetterPrice | null;
  quote: { id: string; reference: string; status: string; total: number | null; currency: string; supersededBy: string | null } | null;
  autoRevise: boolean;
};

export const fetchBetterPrice = async (rfqId: string): Promise<BetterPriceView> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${rfqId}/better-price`, { method: "GET" });
  return parseOrThrow(response);
};

/** Ask the system to write (or rewrite) the "we got you a better price" customer email. */
export const proposeBetterPriceEmail = async (rfqId: string): Promise<{ proposal: BetterPriceProposal }> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${rfqId}/better-price/propose`, { method: "POST", jsonBody: {} });
  return parseOrThrow(response);
};

/**
 * Approve: the next quote version is created with the better items and the
 * (edited) email is held in the Customer email panel, ready to send.
 */
export const approveBetterPrice = async (
  rfqId: string,
  email: { subject: string; text: string } | null,
): Promise<{ quote: { id: string; reference: string; version: string }; revised: boolean; changes: string[]; preparedCustomerEmail: PreparedCustomerEmail | null; betterPrice: BetterPrice | null }> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${rfqId}/better-price/approve`, { method: "POST", jsonBody: email || {} });
  return parseOrThrow(response);
};

export const dismissBetterPrice = async (rfqId: string, reason: string): Promise<{ betterPrice: BetterPrice }> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${rfqId}/better-price/dismiss`, { method: "POST", jsonBody: { reason } });
  return parseOrThrow(response);
};

/** Check the customer's website again (after editing the email or a stated URL). */
export const recheckCustomerWebsite = async (
  rfqId: string,
  url?: string,
): Promise<{ website: RfqCustomerWebsite | null }> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${rfqId}/customer-website`,
    { method: "POST", jsonBody: url ? { url } : {} },
  );
  return parseOrThrow(response);
};

export const loadRfq = async (
  id: string,
): Promise<{ rfq: Rfq; vendorRequests?: RfqVendorRequestRow[] }> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${id}`, {
    method: "GET",
  });
  const body = await parseOrThrow(response);
  return { rfq: body.rfq, vendorRequests: body.vendorRequests };
};

/** Kono operators only: quote the customer now from what is available. */
export type PreparedCustomerEmail = {
  subject: string;
  text: string;
  to: string;
  cc: string[];
  from: { email: string; name: string };
  quoteUrl: string;
  preparedAt: string | null;
  preparedBy: string | null;
  sentAt: string | null;
  sentBy: string | null;
  /** Later sends of the same email ("Send the original email again"), newest last. */
  resends?: { at: string; by: string }[];
};

/**
 * Generate the quote now. By default the customer email is only PREPARED
 * (kept on the RFQ for the operator to review and send); `deliver: "send"`
 * emails the customer at once.
 */
export const generateRfqQuote = async (
  id: string,
  deliver: "prepare" | "send" = "prepare",
): Promise<{
  status: RfqStatus;
  quoteId: string | null;
  quoteReference: string | null;
  unavailableLineIds: string[];
  preparedCustomerEmail: PreparedCustomerEmail | null;
  /** Set when nothing could be quoted: the RFQ stays open and this says why. */
  lastQuoteAttempt?: RfqQuoteAttempt | null;
}> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${id}/generate-quote`,
    { method: "POST", jsonBody: { deliver } },
  );
  return parseOrThrow(response);
};

/**
 * Kono operators: quote a quoted RFQ again from the picks as they stand. The
 * customer's quote becomes the next version when they already saw it; the
 * email is prepared, not sent.
 */
export const regenerateRfqQuote = async (
  id: string,
): Promise<{
  status: RfqStatus;
  quoteId: string;
  quoteReference: string;
  outcome: "created" | "replaced" | "revised" | "additional";
  unavailableLineIds: string[];
  preparedCustomerEmail: PreparedCustomerEmail | null;
}> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${id}/regenerate-quote`,
    { method: "POST", jsonBody: {} },
  );
  return parseOrThrow(response);
};

/** The quote email as the customer gets it (prepared, sent, or a fresh draft). Never sends. */
export type CustomerEmailState = "draft" | "prepared" | "sent" | "already-sent-automatically";

/** One quoted line as the customer reads it: asked vs offered. */
export type QuoteLineOffer = {
  requested: string;
  offered: string;
  differences: string[];
  notes: string[];
};

export const fetchRfqCustomerEmail = async (
  id: string,
): Promise<{
  email: PreparedCustomerEmail;
  state: CustomerEmailState;
  /** "standard" | "edits": whether earlier operator edits shaped the draft. */
  basis?: string;
  examplesUsed?: number;
  offers?: QuoteLineOffer[];
}> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${id}/customer-email`, { method: "GET" });
  return parseOrThrow(response);
};

export const sendRfqCustomerEmail = async (
  id: string,
  /** cc: extra addresses the operator typed, comma-separated (added to the copies the system already sends); again: send an email the customer already received one more time. */
  edits: { subject?: string; text?: string; cc?: string; again?: boolean },
): Promise<{ ok: boolean; preparedCustomerEmail: PreparedCustomerEmail; learned?: boolean }> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${id}/customer-email/send`, {
    method: "POST",
    jsonBody: edits,
  });
  return parseOrThrow(response);
};

// --- customer emails: held suggestions, sent + tracked, follow-ups -------------

export type CustomerEmailStatus = "suggested" | "sent" | "dismissed";
export type CustomerFollowUpTrigger = "no-open" | "after-click" | "after-purchase";

export type CustomerEmailTracking = {
  deliveredAt: string | null;
  opens: number;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  clicks: number;
  firstClickedAt: string | null;
  lastClickedAt: string | null;
  bouncedAt: string | null;
  bounceReason: string | null;
};

export type RfqCustomerEmail = {
  id: string;
  rfqId: string;
  kind: string;
  status: CustomerEmailStatus;
  to: string;
  cc: string[];
  from: { email: string; name: string };
  subject: string;
  text: string;
  reason: string;
  createdAt: string;
  suggestedBy: string | null;
  sentAt: string | null;
  sentBy: string | null;
  dismissedAt: string | null;
  dismissedBy: string | null;
  edited: boolean;
  links: string[];
  tracking: CustomerEmailTracking;
  followUp: {
    trigger: CustomerFollowUpTrigger;
    triggerAt: string;
    parentEmailId: string;
    dueAt: string;
    configuredDelay: number;
    delaySource: "configured" | "learned" | "operator";
  } | null;
  followUpPending: boolean;
};

export type CustomerFollowUpTiming = {
  trigger: CustomerFollowUpTrigger;
  delay: number;
  source: "configured" | "learned";
  unit: "business days" | "hours";
  learned: { samples: number; avgDelay: number; lastAt: string | null };
};

export const fetchRfqCustomerEmails = async (
  id: string,
): Promise<{ emails: RfqCustomerEmail[]; automaticSendingAllowed: boolean; timing: CustomerFollowUpTiming[] }> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${id}/customer-emails`, { method: "GET" });
  return parseOrThrow(response);
};

export const sendSuggestedCustomerEmail = async (
  id: string,
  emailId: string,
  edits: { subject?: string; text?: string; cc?: string },
): Promise<{ ok: boolean; email: RfqCustomerEmail }> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${id}/customer-emails/${encodeURIComponent(emailId)}/send`, {
    method: "POST",
    jsonBody: edits,
  });
  return parseOrThrow(response);
};

export const dismissSuggestedCustomerEmail = async (id: string, emailId: string): Promise<{ ok: boolean; email: RfqCustomerEmail }> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${id}/customer-emails/${encodeURIComponent(emailId)}/dismiss`, {
    method: "POST",
    jsonBody: {},
  });
  return parseOrThrow(response);
};

export const suggestCustomerFollowUpNow = async (
  id: string,
  trigger: CustomerFollowUpTrigger,
): Promise<{ ok: boolean; email: RfqCustomerEmail }> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${id}/customer-emails/suggest-follow-up`, {
    method: "POST",
    jsonBody: { trigger },
  });
  return parseOrThrow(response);
};

export const setRfqFulfillmentChoice = async (
  id: string,
  choice: "send-now" | "send-at-deadline" | "hold",
): Promise<{
  choice: string;
  status: RfqStatus;
  quoteId?: string | null;
  quoteReference?: string | null;
}> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${id}/fulfillment-choice`,
    { method: "POST", jsonBody: { choice } },
  );
  return parseOrThrow(response);
};

export const sendRfqPartialDeadlineDecision = async (
  id: string,
  body: { action: "send-quote" | "custom-email"; subject?: string; body?: string },
): Promise<{
  action: string;
  sent?: boolean;
  status: RfqStatus;
  quoteId?: string | null;
  quoteReference?: string | null;
}> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${id}/partial-deadline-decision`,
    { method: "POST", jsonBody: body },
  );
  return parseOrThrow(response);
};

export const fetchRfqBuyerOptions = async (
  id: string,
): Promise<{
  options: BuyerLineOptions[];
  quote: RfqInlineQuote | null;
  showOptions: boolean;
  showQuoteImmediately: boolean;
  allowAddToCart: boolean;
  allowSaveQuote: boolean;
}> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${id}/buyer-options`,
    { method: "GET" },
  );
  return parseOrThrow(response);
};

export const confirmRfqQuote = async (
  id: string,
  linePreferences: { [lineId: string]: string },
): Promise<{
  quote: RfqInlineQuote;
  quoteId: string;
  quoteReference: string;
  status: RfqStatus;
  options: BuyerLineOptions[];
  allowAddToCart: boolean;
  allowSaveQuote: boolean;
}> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${id}/confirm-quote`,
    { method: "POST", jsonBody: { linePreferences } },
  );
  return parseOrThrow(response);
};

export type UnknownItemDrafts = {
  reject: { subject: string; body: string };
  "ask-more-time": { subject: string; body: string };
  "promise-find": { subject: string; body: string };
};

export const fetchUnknownItemDrafts = async (
  id: string,
): Promise<{ drafts: UnknownItemDrafts; lineIds: string[] }> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${id}/unknown-item-drafts`,
    { method: "POST", jsonBody: {} },
  );
  return parseOrThrow(response);
};

export const sendUnknownItemDecision = async (
  id: string,
  body: {
    action: "reject" | "ask-more-time" | "promise-find";
    subject?: string;
    body?: string;
    extraBusinessHours?: number;
  },
): Promise<void> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${id}/unknown-item-decision`,
    { method: "POST", jsonBody: body },
  );
  await parseOrThrow(response);
};

export type SaveRfqBody = {
  customer: RfqCustomer;
  priority: RfqPriority;
  lines: RfqLine[];
  neededBy: string;
  deliveryLocation: string;
  notes: string;
  sourceFile?: { filename: string; filetype: string } | null;
  sourceText?: string;
};

export const createRfq = async (body: SaveRfqBody): Promise<Rfq> => {
  const response = await sendAuthenticatedRequest(BASE, {
    method: "POST",
    jsonBody: body,
  });
  return (await parseOrThrow(response)).rfq;
};

export const updateRfq = async (
  id: string,
  body: SaveRfqBody,
): Promise<Rfq> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${id}`, {
    method: "PUT",
    jsonBody: body,
  });
  return (await parseOrThrow(response)).rfq;
};

export const deleteRfq = async (id: string): Promise<void> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${id}`, {
    method: "DELETE",
  });
  await parseOrThrow(response);
};

export type VendorOutreachDraft = {
  supplierId: string;
  supplierName: string;
  emails: string[];
  /** Extra copy recipients typed by the operator (empty by default). */
  cc?: string[];
  /** What the operator is typing in the To / Cc boxes (kept verbatim so commas and semicolons survive while typing; UI only). */
  emailsText?: string;
  ccText?: string;
  subject: string;
  body: string;
  introduceKono: boolean;
  lineIds: string[];
  materials: string[];
  /** First name of the sales contact greeted in the body; empty = "Hi," (no contact on file). */
  contactName?: string;
};

export const fetchRfqOperatorFlags = async (): Promise<{
  previewVendorEmails: boolean;
  disableVendorRfqs?: boolean;
}> => {
  const response = await sendAuthenticatedRequest(`${BASE}/operator-flags`, {
    method: "GET",
  });
  return parseOrThrow(response);
};

export const fetchVendorEmailPreview = async (
  id: string,
): Promise<{
  drafts: VendorOutreachDraft[];
  fromEmail: string;
  fromName: string;
  /** The team mailbox copied on every vendor email when an operator sends. */
  cc?: string | null;
}> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${id}/vendor-email-preview`,
    { method: "POST", jsonBody: {} },
  );
  return parseOrThrow(response);
};

export const sendVendorEmails = async (
  id: string,
  drafts: Array<{
    supplierId: string;
    emails: string[];
    cc?: string[];
    subject: string;
    body: string;
  }>,
): Promise<{ vendorsContacted: number }> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${id}/send-vendor-emails`,
    { method: "POST", jsonBody: { drafts } },
  );
  return parseOrThrow(response);
};

export const submitRfq = async (
  id: string,
  mode: "quote" | "vendors" = "quote",
  drafts?: Array<{
    supplierId: string;
    emails: string[];
    cc?: string[];
    subject: string;
    body: string;
  }>,
): Promise<SubmitOutcome> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${id}/submit`, {
    method: "POST",
    jsonBody: { mode, drafts },
  });
  return parseOrThrow(response);
};

/** Requested vs item for one attribute; status drives the colour (match green, differs red, note amber, unknown grey). */
export type MatchFacet = {
  key: string;
  label: string;
  requested: string;
  item: string;
  status: "match" | "differs" | "note" | "unknown";
};

/** Kono-internal: one live inventory option for one RFQ line. */
export type RfqLineOption = {
  productId: string;
  summary: string | null;
  vendor: { supplierId: string | null; name: string | null };
  price: { value: number; unit?: string; currency?: string } | null;
  lineAmount: number | null;
  leadTimeDaysMin: number | null;
  leadTimeDaysMax: number | null;
  leadTimeSource?: string | null;
  obtainedAt: string | null;
  ingestMethod: string;
  priceOrigin: string;
  /** The vendor's published price when it differs from the shown price. */
  vendorOriginalPrice?: { value: number; unit?: string; currency?: string } | null;
  /** Comparable price per pound in the option's currency; null with a reason in pricePerLbNote. */
  pricePerLb?: { value: number; currency: string; basis: string } | null;
  pricePerLbNote?: string | null;
  /** Lowest price per pound among the line's options in the same currency. */
  cheapestPerLb?: boolean;
  sourceUrl: string | null;
  /** Operator's sourcing note on the row; Kono-internal. */
  sourcingNote: string | null;
  exactMatch: boolean;
  differences: string[];
  notes: string[];
  facets?: MatchFacet[];
  selected: boolean;
  /** The platform's pick among this line's options. */
  recommended?: boolean;
  /** What past operator picks say about this vendor. */
  learned?: string | null;
  /** From the experienced purchasers' past picks for this alloy and shape. */
  suggestion?: "strong" | "weak" | null;
};

/** A vendor quote that arrived for the line but cannot be offered as it stands. */
export type RejectedVendorItem = {
  productId: string;
  supplierId: string | null;
  supplierName: string | null;
  summary: string;
  price: string | null;
  reasons: string[];
};

/** Kono-internal: a vendor worth asking about a line, and why. */
export type RfqVendorSuggestion = {
  supplierId: string;
  name: string;
  hasEmail: boolean;
  reasons: string[];
  suggested: boolean;
  /** inventory = catalog match; line-card = on a verified supplier line card; archive = California Metals mailbox record; llm = model guess. */
  source?: "inventory" | "line-card" | "past-rfq" | "archive" | "llm" | "catalog";
  /** The source in operator words, e.g. "Inventory catalog", "Verified line card". */
  sourceLabel?: string;
  /** When the evidence behind the recommendation was last updated (ISO). */
  sourceUpdatedAt?: string | null;
  /** Where the evidence came from ("line card from <file> by <who>"). */
  sourceDetail?: string;
  lineCardId?: string;
  /** The Industry Graph entity behind an archive suggestion. */
  archiveEntityId?: string;
  website?: string;
  email?: string;
  phone?: string;
  introSubject?: string;
  introBody?: string;
  /** The vendor's line card rules this material out; shown with the reason, never asked. */
  excluded?: boolean;
  /** ISO country when known; "" when unknown. */
  country?: string;
  /** Outside the US: listed last, never auto-checked. */
  abroad?: boolean;
  /** Evidence points behind the ordering; higher first. */
  rank?: number;
  /** quoted = quoted Kono or California Metals (green); website = scraped / line card (yellow); ai = model proposal, never quoted (gray). */
  provenance?: "quoted" | "website" | "ai";
  provenanceLabel?: string;
};

export type RfqLineVerdict = "exact" | "close" | "unavailable";

export type RfqLineOptions = {
  lineId: string;
  verdict: RfqLineVerdict;
  options: RfqLineOption[];
  /** Which option the platform would pick, and why. */
  recommendation?: { productId: string | null; why: string } | null;
  /** Vendor answers that cannot be quoted as they stand, and why. */
  rejectedVendorItems?: RejectedVendorItem[];
  vendorSuggestions: RfqVendorSuggestion[];
};

/**
 * Operators: the vendor that fills one line and why they were picked. The
 * reason is kept and shapes later recommendations.
 */
export const selectRfqLineVendor = async (
  rfqId: string,
  lineId: string,
  input: { productId: string; reason: string },
): Promise<void> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${rfqId}/lines/${lineId}/select-vendor`,
    { method: "POST", jsonBody: input },
  );
  await parseOrThrow(response);
};

/**
 * Operators: offer one more inventory item on the quote next to the chosen
 * one (or stop offering it). The customer sees both and may switch.
 */
export const setRfqLineAlsoOffer = async (
  rfqId: string,
  lineId: string,
  input: { productId: string; offer: boolean },
): Promise<{ alsoOfferProductIds: string[] }> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${rfqId}/lines/${lineId}/also-offer`,
    { method: "POST", jsonBody: input },
  );
  return parseOrThrow(response);
};

/** Operators: put a closed RFQ back in front of the vendors. */
export const reopenRfq = async (
  rfqId: string,
): Promise<{ vendorRequestsReopened: number }> => {
  const response = await sendAuthenticatedRequest(`${BASE}/${rfqId}/reopen`, {
    method: "POST",
    jsonBody: {},
  });
  return parseOrThrow(response);
};

/**
 * Kono-internal: the sourcing picture for the lines as they stand on screen
 * (no save needed) — verdict, live options, vendor suggestions.
 */
export const fetchRfqMatchPreview = async (
  lines: RfqLine[],
  priority: RfqPriority,
): Promise<RfqLineOptions[]> => {
  const response = await sendAuthenticatedRequest(`${BASE}/match-preview`, {
    method: "POST",
    jsonBody: { lines, priority },
  });
  return (await parseOrThrow(response)).lines;
};

/** A catalog vendor as the sourcing panel's "add a vendor" search returns it. */
export type CatalogVendorHit = {
  supplierId: string;
  name: string;
  hasEmail: boolean;
  email?: string;
  website?: string;
  phone?: string;
  country?: string;
};

/**
 * Kono-internal: catalog vendors whose name contains the query, to add one
 * the matching did not propose to a line's "Vendors to RFQ".
 */
export const searchRfqCatalogVendors = async (
  query: string,
): Promise<CatalogVendorHit[]> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/vendor-search?q=${encodeURIComponent(query)}`,
    { method: "GET" },
  );
  return (await parseOrThrow(response)).vendors as CatalogVendorHit[];
};

export type CustomerCandidateSource = "user" | "rfq" | "crm";

/** A known person/company the operator can pick to fill the customer block. */
export type CustomerCandidate = {
  key: string;
  source: CustomerCandidateSource;
  sourceLabel: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  address: Address | null;
  website: string;
  detail: string;
  contacts: Array<{ name: string; title: string; email: string; phone: string }>;
};

/**
 * Kono-internal: people and companies the platform already knows (kono.ai
 * users, earlier RFQ/quote contacts, CRM accounts) whose name, company,
 * email or domain contains the query.
 */
export const searchRfqCustomers = async (
  query: string,
): Promise<CustomerCandidate[]> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/customer-search?q=${encodeURIComponent(query)}`,
    { method: "GET" },
  );
  return (await parseOrThrow(response)).customers as CustomerCandidate[];
};

/**
 * Kono-internal: file an email for a vendor that has none, so it becomes
 * RFQ-able from the sourcing panel.
 */
export const saveRfqVendorEmail = async (
  supplierId: string,
  email: string,
  extras?: { name?: string; website?: string; phone?: string },
): Promise<void> => {
  const response = await sendAuthenticatedRequest(`${BASE}/vendor-email`, {
    method: "POST",
    jsonBody: { supplierId, email, ...extras },
  });
  await parseOrThrow(response);
};

/** Persist a model-suggested vendor and return the Kono introduction draft. */
export const prepareRfqLlmVendor = async (params: {
  supplierId: string;
  name: string;
  email?: string;
  website?: string;
  phone?: string;
  line: RfqLine;
  /** When known, the draft is signed with this RFQ's configured sender. */
  rfqId?: string;
}): Promise<{
  supplierId: string;
  hasEmail: boolean;
  draft: { subject: string; body: string };
}> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/llm-vendor-prepare`,
    {
      method: "POST",
      jsonBody: params,
    },
  );
  return (await parseOrThrow(response)) as {
    supplierId: string;
    hasEmail: boolean;
    draft: { subject: string; body: string };
  };
};

export const draftCustomerEmail = async (
  id: string,
): Promise<{ subject: string; body: string }> => {
  const response = await sendAuthenticatedRequest(
    `${BASE}/${id}/customer-email-draft`,
    { method: "POST", jsonBody: {} },
  );
  return (await parseOrThrow(response)).draft;
};

/** "wall_thickness" → "wall thickness" and the choice marker → readable text. */
export const missingFieldLabel = (field: string): string => {
  if (field.startsWith("dimension-choice:")) {
    return `at least two of: ${field.slice("dimension-choice:".length)}`;
  }
  if (field.startsWith("dimension:")) {
    return field.slice("dimension:".length).replace(/_/g, " ");
  }
  return field;
};

/** The dimension inputs to show for a shape, mirroring the server's rules. */
export const DIMENSIONS_BY_SHAPE: { [shape: string]: string[] } = {
  "round bar": ["diameter", "length"],
  rod: ["diameter", "length"],
  wire: ["diameter", "length"],
  sheet: ["thickness", "width", "length"],
  plate: ["thickness", "width", "length"],
  foil: ["thickness", "width", "length"],
  "flat bar": ["thickness", "width", "length"],
  "square bar": ["width", "length"],
  "hex bar": ["width", "length"],
  "round tube": ["outer_diameter", "inner_diameter", "wall_thickness", "length"],
  pipe: ["outer_diameter", "inner_diameter", "wall_thickness", "length"],
  "square tube": ["outer_width", "outer_height", "wall_thickness", "length"],
  ring: ["inner_diameter", "outer_diameter", "thickness"],
  beam: ["height", "width", "length"],
};

export const SHAPE_OPTIONS = Object.keys(DIMENSIONS_BY_SHAPE);
